import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import { isSameDay, isAfter, parseISO } from "date-fns";
import isISODate from "is-iso-date";

import {
  setDateRange,
  setNumRooms,
  setNumPersons,
  setHotelResults,
  setBackdropValue,
  setInputBoxVisibility,
} from "store";
import { visbook } from "api";
import WP from "WP";
import { scrollWindowTo } from "helpers";
import useIsMobile from "hooks/useIsMobile";

import IconSelector from "partials/IconSelector";
import Container from "partials/Container";
import GridElement from "partials/GridElement";
import Button from "partials/Button";
import LoadingButton from "partials/LoadingButton";
import DateRangePicker from "./DateRangePicker";
import GuestGroup from "./GuestGroup";
import MobileGuestGroup from "./MobileGuestGroup";
import Arrow from "icons/ArrowIcon";
import Media from "css/Media";

const AvailabilityChecker = (props) => {
  const [valid, setValid] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const { availability } = props;
  const open = props.inputBoxVisibility;

  useEffect(() => {
    return () => {
      props.setInputBoxVisibility(false);
    };
  }, []);

  const getDateValidity = () => {
    let today = new Date();
    let isFutureDate;
    let isEqual;
    let isISO = isISODate(availability.fromDate);
    if (isISO) {
      isFutureDate = isAfter(parseISO(availability.fromDate), today);
      isEqual = isSameDay(parseISO(availability.fromDate), today);
    } else {
      isFutureDate = isAfter(availability.fromDate, today);
      isEqual = isSameDay(availability.fromDate, today);
    }

    if (isFutureDate || isEqual) return true;
    return false;
  };

  const getErrorText = () => {
    if (!availability.fromDate || !availability.toDate || !getDateValidity())
      return WP.trans.noDateSpan;
    if (!availability.numRooms) return WP.trans.noNumRooms;
    if (!availability.numPersons) return WP.trans.noNumPersons;
  };

  const handleRoomChange = (name, value) => {
    if (name === "numRooms") {
      props.setNumRooms(value);
    }
    if (name === "numPersons") {
      props.setNumPersons(value);
    }
    setValid(true);
  };

  const handleDateChange = (dateObjects, dateStr, instance) => {
    if (dateObjects.length > 1) {
      let from = dateObjects[0];
      let to = dateObjects[1];
      let isEqual = isSameDay(from, to);
      if (!isEqual) {
        props.setDateRange(from, to);
      } else {
        props.setDateRange(null, null);
      }
    }
    setValid(true);
  };

  const handleGuestGroupClick = () => {
    props.setBackdropValue(true);
    props.setInputBoxVisibility(true);
    let guestGroup = document.getElementById("availability-search");
    if (!isMobile) scrollWindowTo(guestGroup, 400, "easeInOutCubic");
  };

  const handleSubmit = () => {
    setSubmitting(true);
    if (
      getDateValidity() &&
      availability.fromDate &&
      availability.toDate &&
      availability.numRooms &&
      availability.numPersons
    ) {
      setValid(true);
      handleVisbookSearch();
    } else {
      setValid(false);
      setSubmitting(false);
    }
  };

  const handleVisbookSearch = () => {
    visbook
      .webproducts({
        fromDate: availability.fromDate,
        toDate: availability.toDate,
        numRooms: availability.numRooms,
        numPersons: availability.numPersons,
      })
      .then((resp) => {
        props.setHotelResults(resp);
        setSubmitting(false);
        props.setInputBoxVisibility(false);
      });
  };

  return (
    <Wrapper id='availability-search'>
      <Container grid>
        <GridElement
          colStart={{ default: 1, s: 5 }}
          colEnd={{ default: 8, s: 20 }}
          align='center'
        >
          <Container flex align='center' wrap={isMobile} direction='row'>
            <FlexElement first>
              <DateRangePicker
                fromDate={availability.fromDate}
                toDate={availability.toDate}
                valid={valid}
                dateHandler={(string, dateStr, instance) =>
                  handleDateChange(string, dateStr, instance)
                }
              />
            </FlexElement>
            <FlexElement second>
              <StyledLabel>
                <IconSelector icon='user' />
                <InputWrapper>
                  <StyledMockInput
                    onClick={handleGuestGroupClick}
                    valid={valid}
                  >
                    {availability.numRooms && availability.numPersons
                      ? `${availability.numRooms} ${WP.trans.rooms}, ${availability.numPersons} ${WP.trans.guests}`
                      : WP.trans.amountOfGuests + ": "}
                  </StyledMockInput>
                  {open && !isMobile && (
                    <GuestGroup
                      numRooms={availability.numRooms}
                      numPersons={availability.numPersons}
                      handleChange={(name, value) =>
                        handleRoomChange(name, value)
                      }
                    />
                  )}
                </InputWrapper>
              </StyledLabel>
            </FlexElement>
            <FlexElement last>
              {submitting ? (
                <LoadingButton
                  placeholder={isMobile ? "" : WP.trans.search}
                  fill='#439758'
                  negative
                />
              ) : (
                <Button negative disabled={!valid} onClick={handleSubmit}>
                  {!isMobile ? WP.trans.search : <StyledArrow />}
                </Button>
              )}
            </FlexElement>
          </Container>
        </GridElement>
        {!valid && <ErrorText>{getErrorText()}</ErrorText>}
      </Container>
      {open && isMobile && (
        <MobileGuestGroup
          numRooms={availability.numRooms}
          numPersons={availability.numPersons}
          handleChange={(name, value) => handleRoomChange(name, value)}
          handleSubmit={handleSubmit}
          submitting={submitting}
        />
      )}
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  availability: state.availability,
  backdropValue: state.backdropValue,
  inputBoxVisibility: state.inputBoxVisibility,
});

const actions = {
  setDateRange,
  setNumPersons,
  setNumRooms,
  setHotelResults,
  setBackdropValue,
  setInputBoxVisibility,
};

export default connect(mapStateToProps, actions)(AvailabilityChecker);

const Wrapper = styled.section`
  > .container {
    .container {
      ${Media.greaterThan("s")`
        padding: 0 48px;
      `}
    }
  }
`;

const FlexElement = styled.div`
  ${(props) =>
    props.first &&
    css`
      width: 100%;
      margin-right: ${(props) => props.theme.spacing.contentXL};
    `}
  ${(props) =>
    props.second &&
    css`
      flex: 1;
    `}
    ${(props) =>
    !props.first &&
    css`
      width: auto;
    `}
    
    ${Media.greaterThan("s")`
        flex: ${(props) => (props.last ? 0 : 1)};
    `}
`;

const StyledLabel = styled.label`
  display: flex;
`;

const StyledArrow = styled(Arrow)`
  fill: ${(props) => props.theme.colors.button};
`;

const StyledMockInput = styled.div`
  cursor: pointer;
  border-bottom: 1px solid ${(props) => props.theme.colors.lines};
  box-sizing: border-box;
  padding: ${(props) => props.theme.spacing.contentXS};
  height: 100%;
  display: flex;
  align-items: center;
  color: ${(props) =>
    props.valid ? props.theme.colors.text : props.theme.colors.button};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-right: ${(props) => props.theme.spacing.contentXL};
`;

const ErrorText = styled.p`
  margin-top: 16px;
  background-color: ${(props) => props.theme.colors.tooltip};
  padding: ${(props) => props.theme.spacing.contentL};
  border-radius: ${(props) => props.theme.spacing.contentM};
  grid-column: 1/8;
  grid-row: 4;

  ${Media.greaterThan("s")`
    grid-column: 14/22;
    grid-row: auto;
  `}
`;
